<template>
    <div>
        <TheBanner :title="this.$lang.catalogTitle"
                   :description="this.$lang.catalogSubtitle"
                   image="cover.jpeg"/>
        <div id="transition-container" class="body-block">
            <div id="category-picker"
                 v-if="ready"
                 :class="{hidden: displayedCategory !== null}">
                <div>{{this.$lang.catalogPrompt}}</div>
                <div class="block-title">
                    {{this.$lang.mainBlock}}
                </div>
                <GridView size="155px"
                          :constant-size="true"
                          class="category-picker-grid"
                          :data="mainGridData"/>
                <div class="block-title">
                    {{this.$lang.otherBlock}}
                </div>
                <GridView size="150px"
                          :constant-size="true"
                          class="category-picker-grid"
                          :data="otherGridData"/>
            </div>
            <div id="catalog"
                 v-if="ready"
                 :class="{hidden: displayedCategory === null}">
                <div id="tab-container">
                    <router-link v-for="category in mainCategories"
                                 :class="{tab: true, active: category === displayedCategory}"
                                 :key="category.index"
                                 :to="`/catalog/${category.index}`"
                                 @click.native="animateProductView">
                        {{category.title}}
                    </router-link>
                    <div class="hr"></div>
                    <router-link v-for="category in otherCategories"
                                 :class="{tab: true, active: category === displayedCategory}"
                                 :key="category.index"
                                 :to="`/catalog/${category.index}`"
                                 @click.native="animateProductView">
                        {{category.title}}
                    </router-link>
                </div>
                <div id="product-view"
                     v-if="displayedCategory !== null">
                    <div v-if="displayedCategory.search">
                        <input v-model="searchTerm"
                               @input="filter"
                               id="search-input"
                               :placeholder="$lang.catalogSearch"/>
                    </div>
                    <div id="product-view-meta-container"
                         v-else>
                        <image-view class="img"
                                    size="medium"
                                    v-if="displayedCategory.image"
                                    :url="displayedCategory.image"
                                    :alt="displayedCategory.title"/>
                        <img v-else
                             class="svg"
                             :src="require(`@/assets/${displayedCategory.icon}-24px.svg`)"
                             :alt="displayedCategory.title"/>
                        <div id="product-view-meta">
                            <div id="product-view-title">
                                {{displayedCategory.title}}
                            </div>
                            <div id="product-view-desc">
                                {{displayedCategory.description}}
                            </div>
                        </div>
                    </div>
                    <router-link id="back-button"
                                 class="button"
                                 to="/catalog/">
                        {{this.$lang.catalogBack}}
                    </router-link>
                    <GridView id="grid-view"
                              size="150px"
                              :constant-size="true"
                              :data="displayedCategory.items"/>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
	import TheBanner from "@/components/TheBanner";
	import GridView from "@/components/GridView";
	import ImageView from "@/components/ImageView";

	export default {
		components: {ImageView, TheBanner, GridView},
		props: {
			dbData: {
				type: Object,
				required: true
			}
		},
		created() {
			if (this.dbData.categories) {
				this.updateDisplayed()
			}
		},
		watch: {
			$route() {
				this.updateDisplayed();
			},
			'dbData.categories'() {
				this.updateDisplayed();
			}
		},
		data() {
			return {
				displayedCategory: null,
				searchTerm: "",
				searchCategory: {
					title: this.$lang.catalogSearch,
					link: `/catalog/search`,
					index: "search",
					icon: "search",
					search: true,
					items: [],
					main: false
				},
				ready: false
			}
		},
		computed: {
			mainGridData() {
				return this.mainCategories.map(this.mapper);
			},
			otherGridData() {
				return this.otherCategories.map(this.mapper);
			},
			mainCategories() {
				return this.dbData.categories
					.filter(cat => cat.main);
			},
			otherCategories() {
				return this.dbData.categories
					.filter(cat => !cat.main)
					.concat([this.searchCategory]);
			},
			allProducts() {
				return this.dbData.categories.reduce(
					(prev, curr) => prev.concat(curr.items), []);
			}
		},
		methods: {
			updateDisplayed() {
				if (this.$route.params.id) {
					if (this.$route.params.id === "search") {
						this.displayedCategory = this.searchCategory;
						this.filter();
					} else {
						for (let item of this.dbData.categories) {
							if (parseInt(item.index) === parseInt(this.$route.params.id)) {
								this.displayedCategory = item;
								break;
							}
						}
					}
				} else {
					this.displayedCategory = null;
				}
				this.ready = true
			},
			animateProductView() {
				let productView = document.getElementById("product-view");
				productView.classList.add("animating");
				setTimeout(() => productView.classList.remove("animating"));
			},
			mapper(cat) {
				return {
					name: cat.title,
					link: cat.link || `/catalog/${cat.index}`,
					image: cat.image,
					icon: cat.icon,
					search: cat.search
				};
			},
			filter() {
				this.displayedCategory.items =
					this.allProducts.filter(x => x["full_name"] &&
						x["full_name"].toLowerCase().indexOf(this.searchTerm.toLowerCase()) !== -1);
			}
		}
	}
</script>

<style lang="scss" scoped>
    #transition-container {
        position: relative;
        display: flex;
        flex-flow: row nowrap;

        @media #{$tablet} {
            overflow-x: visible;
            padding-top: 32px;
        }

        @media #{$tablet-inv} {
            padding-top: 64px;
        }
    }

    #category-picker {
        position: relative;
        left: 0;
        min-width: 100%;
        padding-bottom: 4px;
        transition: transform 0.5s, opacity 0.5s, max-height 0.5s;
        transition-timing-function: $bezier;
        overflow-y: hidden;
    }

    #category-picker.hidden {
        transform: translateX(-2em);
        opacity: 0;
        pointer-events: none;

        @media #{$tablet} {
            max-height: calc(100vh - 240px);
        }

        @media #{$tablet-inv} {
            max-height: calc(65vh - 192px);
        }
    }

    .block-title {
        margin: 48px 0 16px 0;
        font-family: $serif;
        font-weight: 700;
        font-size: 24px;
    }

    .category-picker-grid {
        font-family: $serif;
    }

    #catalog {
        position: relative;
        display: flex;
        flex-flow: row nowrap;
        left: -100%;
        min-width: 100%;
        transition: transform 0.5s, opacity 0.5s;
        transition-timing-function: $bezier;
        align-items: flex-start;
    }

    #catalog.hidden {
        transform: translateX(2em);
        opacity: 0;
        pointer-events: none;
        min-width: calc(100% - 2em);
    }

    #tab-container {
        position: sticky;
        display: flex;
        flex-flow: column nowrap;
        top: 128px;
        width: 180px;
        min-width: 180px;
        text-align: right;

        @media #{$tablet} {
            display: none;
        }
    }

    .hr {
        height: 2px;
        width: 100%;
        background-color: $p-accent;
        margin-bottom: 4px;
    }

    .tab {
        position: relative;
        padding: 8px 20px 8px 0;
        font-family: $serif;
        border-right: $dotted;
        margin-bottom: 4px;
    }

    .tab:hover {
        background-color: $p-fg;
        color: $p-bg;
    }

    .tab.active {
        background-color: $p-fg;
        color: $p-bg;
        border-left: solid $p-accent 4px;
    }

    .category-card {
        display: flex;
        flex-flow: column nowrap;
        justify-content: center;
        margin-left: 50px;
        margin-right: 50px;
        text-align: left;
        transition: opacity 1s;
        visibility: visible;
        opacity: 1;
        min-width: 50vw;
    }

    .category-name {
        vertical-align: bottom;
        padding: 5px;
        font-size: 24px;
        font-weight: 700;
        font-family: $serif;
    }

    .category-description {
        padding: 5px;
    }

    #back-button {
        @media #{$tablet-inv} {
            display: none;
        }
        margin-top: 16px;
    }

    #product-view {
        position: relative;
        display: flex;
        flex-flow: column nowrap;
        border-radius: 24px;
        text-align: left;
        flex-grow: 1;
        transition: opacity 0.5s $bezier;

        @media #{$tablet-inv} {
            padding: 0 32px 0 48px;
        }
    }

    #product-view.animating {
        opacity: 0.5;
        transition: unset;
    }

    #search-input {
        font-size: $font-desktop;
        background-color: $p-bg;
        padding: 1px;
        border-radius: 0;
        border-top: none;
        border-left: none;
        border-right: none;
        border-bottom: $solid;
        width: 100%;
        max-width: 360px;
        outline: none;
    }

    #product-view-meta-container {
        img, .img {
            width: 120px;
            height: 120px;
            border-radius: 60px;
            object-fit: cover;
            margin-top: 8px;
            margin-right: 32px;
            z-index: 1;
            @extend %dotted-box;

            @media #{$tablet} {
                width: 80px;
                height: 80px;
                margin-top: 20px;
                margin-right: 16px;
            }
        }

        img.svg {
            padding: 16px;
            box-sizing: border-box;
        }
    }

    #product-view-meta-container {
        display: flex;
        flex-flow: row nowrap;
        align-items: flex-start;
    }

    #product-view-meta {
        display: flex;
        flex-grow: 1;
        flex-flow: column nowrap;
        min-height: 128px;
        justify-content: center;
    }

    #product-view-title {
        font-weight: bold;
        font-family: $serif;
        margin-bottom: 8px;

        @media #{$tablet} {
            font-size: 28px;
            line-height: 26px;
        }

        @media #{$tablet-inv} {
            font-size: 36px;
        }
    }

    #product-view-desc {
        line-height: 24px;
        width: 95%;
        max-width: 800px;
    }

    #grid-view {
        margin-top: 32px;
    }
</style>